// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/buildRailProgressFromPlan.test.ts
import { type AskPunttAgentStep } from "@mg/schemas/src/christo/askPuntt";

import { type AskV2RailProgressItem } from "./types";

import { ASK_V2_PLAN_TOOL_NAME } from "../planTool";

/** The row id of the placeholder shown before the turn's first plan arrives. */
export const ASK_V2_PLAN_STARTING_ID = "plan-starting";

/**
 * The rail row for "the plan is done, the turn is writing its answer".
 *
 * A fixed id, not a plan-derived one: at most one is ever on the rail, and it
 * belongs to no plan step.
 */
export const ASK_V2_PLAN_WRITING_ID = "plan-writing-answer";

/**
 * The rail row for "the plan is done, a tool call is still running".
 *
 * The same position as the writing row and the opposite claim. A turn whose
 * last plan step is ticked is not necessarily writing prose: the model may be
 * mid-call, and with the plan fully ticked the writing row was shown while a
 * `write_plan` call was still open (measured, A-chat t=112-113 of the gate-2
 * run). The row that stands there instead names the call, so the rail says what
 * the thread's own tool row already says rather than claiming the answer.
 *
 * A DIFFERENT id from the writing row on purpose: a DOM-diff harness reads
 * `plan-writing-answer` as "the turn is writing its answer", and that is exactly
 * what this row must not mean.
 */
export const ASK_V2_PLAN_RUNNING_ID = "plan-running-step";

/**
 * The marker on a step the model withdrew (`cancelled`).
 *
 * A MARKER, not the words on screen: this module is pure and has no i18n, so
 * the hook that owns `t` (`useAskV2PlanProgress`) swaps it for
 * `askV2.rail.progressSteps.skipped`. Rendering it raw left untranslated
 * English in all five non-English locales -- every sibling label on this rail
 * goes through `t`, so this one string was the only one the locale-parity test
 * could not see.
 */
export const ASK_V2_PLAN_SKIPPED_DETAIL = "skipped";

/**
 * The harness statuses (`pydantic_ai_harness.planning.TaskStatus`). `blocked`
 * is unreachable while warhol runs `write_plan` alone -- the toolset refuses it
 * without subtasks -- and is carried anyway so a harness change lands as a row
 * rather than as a dropped step.
 */
const PLAN_STEP_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  "cancelled",
  "blocked",
] as const;

type AskV2PlanStepStatus = (typeof PLAN_STEP_STATUSES)[number];

/** A `tool_call` step, which is the only shape that can be in flight. */
export type AskPunttToolCallStep = Extract<
  AskPunttAgentStep,
  { type: "tool_call" }
>;

/** One step of the checklist a `write_plan` call carries. */
export type AskV2PlanStep = {
  id: string;
  content: string;
  status: AskV2PlanStepStatus;
};

const PLAN_STEP_STATUS_SET: ReadonlySet<string> = new Set(PLAN_STEP_STATUSES);
// These are the rejection producers, not display copy: the harness returns
// `Plan not updated:` (pinned in warhol's test_plan.py) and Christo's argument
// validator returns `Arguments rejected:` from warholChat.ts.
const REJECTED_PLAN_RESULT_PREFIXES = [
  "Plan not updated:",
  "Arguments rejected:",
] as const;

function isRejectedPlanResult(summary: unknown): boolean {
  return (
    typeof summary === "string" &&
    REJECTED_PLAN_RESULT_PREFIXES.some((prefix) => summary.startsWith(prefix))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * The plan a `write_plan` call carries, or null when the args are not one.
 *
 * `write_plan` takes `items: list[PlanItem]` and the model passes the WHOLE
 * plan on every call, so one call is a complete snapshot -- there is nothing to
 * merge with the call before it.
 */
function planStepsFromArgs(args: unknown): AskV2PlanStep[] | null {
  if (!isRecord(args)) return null;
  const items = args.items;
  if (!Array.isArray(items)) return null;

  const steps: AskV2PlanStep[] = [];
  for (const item of items) {
    if (!isRecord(item)) continue;
    // Trimmed before the emptiness check: `content` is model-authored and
    // unvalidated, so a step that is only spaces or a stray newline is the same
    // nothing as `""`. Kept as a truthy string it became a blank row on the
    // rail. The rendered return has always read it this way (`(\S.*)` then
    // `.trim()`).
    const content = stringField(item.content).trim();
    if (!content) continue;
    const status = stringField(item.status);
    steps.push({
      // `PlanItem.id` is auto-generated by the harness, but a plan the model
      // wrote without ids still has to key a row; the index covers it.
      id: stringField(item.id) || `plan-step-${steps.length}`,
      content,
      status: PLAN_STEP_STATUS_SET.has(status)
        ? (status as AskV2PlanStepStatus)
        : "pending",
    });
  }
  return steps.length > 0 ? steps : null;
}

/**
 * The step marker the harness renders, mapped back onto its status.
 *
 * `[x]` done, `[~]` in progress, `[ ]` still to come. Anything else (a marker a
 * newer harness adds) reads as `pending`, which claims nothing.
 */
const PLAN_SUMMARY_STATUS: Record<string, AskV2PlanStepStatus> = {
  x: "completed",
  "~": "in_progress",
  " ": "pending",
};

/** `1. [x] Run selected review checks` -- the harness's rendered plan line. */
const PLAN_SUMMARY_LINE = /^\s*\d+\.\s*\[(.)\]\s+(\S.*)$/;

/**
 * The plan off the tool's RENDERED return, or null when it is not one.
 *
 * The same five steps the `tool_call` carries in `args`, as the harness prints
 * them back (shot2 of the Story 4 feedback: `Plan updated: 5 step(s).` then
 * `1. [x] ...` per line, then `(3/5 completed)`).
 *
 * It exists because `args` is not the only shape the plan arrives in. christo
 * builds a turn's steps from the running turn's Temporal history, and
 * `warholAgentSteps` pushes a `tool_result` slot for every settled call
 * whether or not it saw that call's own event -- `if (slot.call)` then
 * `if (slot.result)`, each independent. A call whose `function_tool_call`
 * event fell outside the paginated read therefore reaches the rail as a lone
 * result: no `args`, and the whole plan sitting in `summary`. Reading `args`
 * alone drew "Getting started" over a review that had written its checklist.
 *
 * The trailing budget line (`[budget: 59 of 64 tool calls left]`) does not
 * match the line pattern, so it is neither a step nor able to shift the count.
 */
export function planStepsFromSummary(summary: unknown): AskV2PlanStep[] | null {
  if (typeof summary !== "string" || !summary) return null;

  const steps: AskV2PlanStep[] = [];
  for (const line of summary.split("\n")) {
    const match = PLAN_SUMMARY_LINE.exec(line);
    if (!match) continue;
    const content = (match[2] ?? "").trim();
    if (!content) continue;
    steps.push({
      id: `plan-step-${steps.length}`,
      content,
      status: PLAN_SUMMARY_STATUS[match[1] ?? ""] ?? "pending",
    });
  }
  return steps.length > 0 ? steps : null;
}

/**
 * Every accepted `write_plan` snapshot on these steps, oldest first.
 *
 * Structured args are preferred for a call because they are the plan as the
 * model wrote it, statuses included and never truncated. The rendered summary
 * is only a fallback when that call's args were not persisted. When a rejection
 * result is present in the slice, its call is paired by `stepId` or by unkeyed
 * call/result order and cannot replace the last accepted snapshot. A lone call
 * whose result fell outside the slice is indistinguishable from an accepted
 * call.
 */
function collectPlanSnapshots(
  agentSteps: readonly AskPunttAgentStep[],
): AskV2PlanStep[][] {
  // A write_plan call can carry perfectly parseable items and still be refused
  // by the tool (for example, duplicate ids). Pair calls/results before reading
  // the timeline so a rejected call cannot briefly become canonical.
  const rejectedCallIds = new Set<string>();
  const rejectedUnkeyedCallIndexes = new Set<number>();
  const callIdsWithStructuredArgs = new Set<string>();
  const unkeyedResultIndexesWithStructuredArgs = new Set<number>();
  let pendingUnkeyedStructuredCallIndex: number | null = null;
  for (let index = 0; index < agentSteps.length; index += 1) {
    const step = agentSteps[index];
    if (!step) continue;
    if (step.type === "tool_call" && step.tool === ASK_V2_PLAN_TOOL_NAME) {
      const parsed = planStepsFromArgs(step.args);
      const stepId = step.stepId || null;
      if (stepId && parsed) callIdsWithStructuredArgs.add(stepId);
      // Any write_plan call closes the unkeyed one still waiting for a result,
      // keyed or not: the producers emit a slot's call and its result together
      // (`warholAgentSteps`, `live_steps.py`), so a later unkeyed result-only
      // plan belongs to a slot of its own, never to the stale call. Left set
      // across a keyed slot, that result was suppressed as "already covered by
      // args" written two slots earlier and the rail kept the superseded plan.
      pendingUnkeyedStructuredCallIndex = !stepId && parsed ? index : null;
      continue;
    }
    if (step.type === "tool_result" && step.tool === ASK_V2_PLAN_TOOL_NAME) {
      const stepId = step.stepId || null;
      if (!stepId && pendingUnkeyedStructuredCallIndex != null) {
        unkeyedResultIndexesWithStructuredArgs.add(index);
      }
      if (isRejectedPlanResult(step.summary)) {
        if (stepId) rejectedCallIds.add(stepId);
        else if (pendingUnkeyedStructuredCallIndex != null) {
          rejectedUnkeyedCallIndexes.add(pendingUnkeyedStructuredCallIndex);
        }
      }
      pendingUnkeyedStructuredCallIndex = null;
    }
  }

  const snapshots: AskV2PlanStep[][] = [];
  for (let index = 0; index < agentSteps.length; index += 1) {
    const step = agentSteps[index];
    if (!step) continue;
    if (step.type === "tool_call") {
      const stepId = step.stepId || null;
      if (
        step.tool !== ASK_V2_PLAN_TOOL_NAME ||
        (stepId != null && rejectedCallIds.has(stepId)) ||
        (stepId == null && rejectedUnkeyedCallIndexes.has(index))
      ) {
        continue;
      }
      const parsed = planStepsFromArgs(step.args);
      if (parsed) snapshots.push(parsed);
      continue;
    }
    if (step.type === "tool_result") {
      const stepId = step.stepId || null;
      if (
        step.tool !== ASK_V2_PLAN_TOOL_NAME ||
        isRejectedPlanResult(step.summary) ||
        (stepId != null && rejectedCallIds.has(stepId)) ||
        (stepId != null && callIdsWithStructuredArgs.has(stepId)) ||
        (stepId == null && unkeyedResultIndexesWithStructuredArgs.has(index))
      ) {
        continue;
      }
      const parsed = planStepsFromSummary(step.summary);
      if (parsed) snapshots.push(parsed);
    }
  }
  return snapshots;
}

/**
 * Every accepted plan snapshot on the steps, oldest first.
 *
 * `write_plan` is whole-plan replacement, so ONE call is a complete plan. A turn
 * does not call it once: the model re-emits the whole checklist whenever it
 * advances, and the live thread retains every call (`merge_agent_steps` joins by
 * `stepId` and appends). The gate-2 C1 turn carried SIX of them. Reading only
 * the newest one is what let the rail reopen a plan it had already finished --
 * so the callers that care about the turn rather than the instant read them all.
 */
export function readPlanSnapshots(
  agentSteps: readonly AskPunttAgentStep[] | undefined,
): AskV2PlanStep[][] {
  if (!agentSteps?.length) return [];
  return collectPlanSnapshots(agentSteps);
}

/** The steps of the LATEST `write_plan` call in the turn, or null when none. */
export function readLatestPlanSteps(
  agentSteps: readonly AskPunttAgentStep[] | undefined,
): AskV2PlanStep[] | null {
  if (!agentSteps?.length) return null;
  const snapshots = collectPlanSnapshots(agentSteps);
  return snapshots.length > 0 ? snapshots[snapshots.length - 1]! : null;
}

/**
 * The order a step's own statuses rank in, for the within-turn fold.
 *
 * `completed` outranks `cancelled` so a step the model ticked and then withdrew
 * cannot lose a tick it already showed. `blocked` outranks `in_progress` for the
 * same reason in the other direction: a step that could not run is terminal, so
 * a later snapshot that restates it as `in_progress` must not reopen a row that
 * already said it was stuck. Ranked LEVEL with `in_progress` (as it was while
 * both drew a spinner) the tie is won by whichever status the fold saw FIRST,
 * so a turn that blocked a step after starting it lost the block and the row
 * spun for the rest of the review. All that matters here is that the ladder is
 * total and that nothing outranks `completed`.
 */
const PLAN_STEP_RANK: Record<AskV2PlanStepStatus, number> = {
  pending: 0,
  in_progress: 1,
  blocked: 2,
  cancelled: 3,
  completed: 4,
};

/** A step id the parsers synthesised from the step's POSITION. */
const SYNTHETIC_STEP_ID = /^plan-step-\d+$/;

/**
 * The name one step is folded on within a turn.
 *
 * Its id when a real one came off the wire, and its normalised title otherwise.
 * `planStepsFromArgs` and `planStepsFromSummary` both synthesise POSITIONAL ids
 * (`plan-step-0`) for a step that has none -- which is every step of the gate-2
 * C1 turn -- and folding on those alone would land a re-plan that inserts or
 * reorders a step on the wrong row. The title is what the model actually
 * re-emits verbatim, so it is the identity that survives a re-plan.
 */
function planStepKey(step: AskV2PlanStep): string {
  if (step.id && !SYNTHETIC_STEP_ID.test(step.id)) return `id:${step.id}`;
  return `title:${step.content.trim().replace(/\s+/g, " ").toLowerCase()}`;
}

/**
 * One plan for the whole turn, from every snapshot the turn has written.
 *
 * `write_plan` is whole-plan replacement, and the model re-emits the checklist
 * with a step it has ALREADY finished marked `in_progress` again -- measured on
 * the gate-2 C1 turn, where the rail read 3/3 completed and "Writing the
 * answer…" at t=1150 and then reopened to done/active/pending at t=1160 for 78
 * seconds and re-completed at t=1243. The fourth of that turn's six `write_plan`
 * calls is the one that regressed it.
 *
 * The rule is the product's: a step shown completed in a turn STAYS completed
 * for the rest of that turn, whatever a later call says about it. Every step is
 * therefore raised to the highest status it reached anywhere in the turn, and
 * steps new to a re-plan keep their own status and their own place in the latest
 * order.
 *
 * Order and labels come from the LATEST snapshot: that is the checklist the
 * model is working from, and only its statuses are corrected. A step the latest
 * call DROPPED is dropped here too, whatever it had reached: re-adding it would
 * revive a superseded plan over the one the model is actually working from,
 * which is the same defect wearing the opposite mask.
 */
export function foldPlanSnapshots(
  snapshots: readonly (readonly AskV2PlanStep[])[],
): AskV2PlanStep[] | null {
  if (snapshots.length === 0) return null;

  const best = new Map<string, AskV2PlanStepStatus>();
  for (const snapshot of snapshots) {
    for (const step of snapshot) {
      const key = planStepKey(step);
      const current = best.get(key);
      if (!current || PLAN_STEP_RANK[step.status] > PLAN_STEP_RANK[current]) {
        best.set(key, step.status);
      }
    }
  }

  const latest = snapshots[snapshots.length - 1]!;
  const rows: AskV2PlanStep[] = [];
  const emitted = new Set<string>();
  for (const step of latest) {
    const key = planStepKey(step);
    if (emitted.has(key)) continue;
    emitted.add(key);
    rows.push({ ...step, status: best.get(key) ?? step.status });
  }
  return rows;
}

/**
 * The newest plan in the messages given, or null when none of them carries one.
 *
 * Give it ONE review cycle, not a whole thread: the caller
 * (`useAskV2PlanProgress`) slices from the latest Start review first, because
 * the newest plan in a thread belongs to the previous review whenever the
 * running turn has not written its own yet — and showing that one ticks rows
 * for work the new review has not done.
 */
export function readLatestPlanStepsFromMessages(
  messages: readonly { agentSteps?: AskPunttAgentStep[] }[] | undefined,
): AskV2PlanStep[] | null {
  let latest: AskV2PlanStep[] | null = null;
  for (const message of messages ?? []) {
    const parsed = readLatestPlanSteps(message.agentSteps);
    if (parsed) latest = parsed;
  }
  return latest;
}

/**
 * The turn's plan, folded over every `write_plan` call the turn has made.
 *
 * Not `readLatestPlanStepsFromMessages`: that answers "what does the newest call
 * say", which is the right question for an instant and the wrong one for a turn
 * whose model re-emits its checklist. See `foldPlanSnapshots`.
 */
export function readTurnPlanSteps(
  messages: readonly { agentSteps?: AskPunttAgentStep[] }[] | undefined,
): AskV2PlanStep[] | null {
  const snapshots: AskV2PlanStep[][] = [];
  for (const message of messages ?? []) {
    snapshots.push(...readPlanSnapshots(message.agentSteps));
  }
  return foldPlanSnapshots(snapshots);
}

/**
 * The turn's newest agent step, when it is a call still waiting for its result.
 *
 * "Latest is a call" is the whole test, and it is the one the producers make
 * true: `merge_agent_steps` (warhol) and `warholAgentSteps` (christo) both emit
 * a slot's call and then its result, in that order, so a call with no result
 * behind it is the last row of the live list exactly while it is in flight.
 *
 * It exists so the rail can tell "the turn is writing its answer" from "the turn
 * is mid-call with every plan step already ticked" -- two states the plan's own
 * statuses cannot separate. Measured on the gate-2 A-chat turn: at t=112 the
 * rail showed "Writing the answer…" over an OPEN `write_plan` call.
 */
export function inFlightAgentStep(
  messages: readonly { agentSteps?: AskPunttAgentStep[] }[] | undefined,
): AskPunttToolCallStep | null {
  let last: AskPunttAgentStep | null = null;
  for (const message of messages ?? []) {
    for (const step of message.agentSteps ?? []) last = step;
  }
  return last && last.type === "tool_call" ? last : null;
}

/**
 * The address the plan's rows are namespaced by, or null for the bare ids.
 *
 * A plan's own step ids are positional (`plan-step-0` is EVERY plan's first
 * step), so two plans in one thread hand out the same row ids. On screen that
 * is invisible -- the old rows are gone by the time the new one lands -- but a
 * DOM-diff harness keyed on the testid reads a new plan's first row as the old
 * plan's first row and reports a plan that never changed.
 *
 * The TURN is the stable name, not the message the steps happen to be attached
 * to: warhol's live snapshot lands on the overlay's synthesized row first
 * (`w<userMessageId>`) and on christo's own placeholder once that exists, so a
 * message-keyed id changes mid-plan and remounts every row under it. The turn's
 * own message id does not move.
 */
export function planScopeKey(
  turn: readonly { _id?: string }[] | null | undefined,
): string | null {
  return turn?.[0]?._id ?? null;
}

function rowStatus(
  status: AskV2PlanStepStatus,
): AskV2RailProgressItem["status"] {
  switch (status) {
    case "completed":
      return "done";
    case "in_progress":
      return "active";
    case "cancelled":
      // Stays on the rail with its label: a step the model withdrew is a thing
      // that happened to the review, and hiding it would make the row count
      // disagree with the plan the model is working from.
      return "done";
    case "blocked":
      // A step that could not finish is OVER, and `active` is a spinner: drawn
      // that way the rail kept turning over a check that is dead for the rest
      // of the turn, and a check that finished by failing never ticked off at
      // all (N4). Its own row says neither "done" (the work did not happen) nor
      // "running" (nothing is still happening) -- `AskV2RightRail` has drawn
      // that third state since it existed, with no producer until now.
      return "blocked";
    default:
      return "pending";
  }
}

/**
 * One rail row per plan step, in the order the model wrote them.
 *
 * `planKey` namespaces the row ids by the plan they belong to. Step ids are
 * positional, so without it a second plan in the same thread reuses
 * `plan-plan-step-0` and a testid-keyed harness cannot tell the two apart; see
 * `latestPlanMessageId`. Optional, and absent means the bare positional id,
 * which is what a single-plan render (and every test that builds one) needs.
 */
export function planStepsToProgressItems(
  steps: readonly AskV2PlanStep[],
  planKey?: string,
): AskV2RailProgressItem[] {
  return steps.map((step) => ({
    id: planKey ? `plan-${planKey}-${step.id}` : `plan-${step.id}`,
    label: step.content,
    ...(step.status === "cancelled"
      ? { detail: ASK_V2_PLAN_SKIPPED_DETAIL }
      : {}),
    status: rowStatus(step.status),
  }));
}

/**
 * The rows a warhol turn's rail shows.
 *
 * `null` plan means the turn has not called `write_plan` yet -- a turn that
 * needs one tool call is never planned, and the model may still be deciding.
 * The caller decides what to show for that (one honest "getting started" row);
 * this function never invents a step.
 */
export function buildAskV2PlanProgress(input: {
  steps: readonly AskV2PlanStep[] | null;
  completed: boolean;
  /** The plan's own address, so two plans cannot share row ids. */
  planKey?: string;
}): AskV2RailProgressItem[] {
  if (!input.steps) return [];
  const items = planStepsToProgressItems(input.steps, input.planKey);
  // A finished review has no active step; the plan is kept so the person can
  // still read what the review did.
  return input.completed
    ? items.map((item) => ({ ...item, status: "done" as const }))
    : items;
}
